import { inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, interval, Observable, Subject, Subscription, tap } from 'rxjs';
import { WebSocketController } from '@services/web-socket.controller';
import { ToastService } from '@services/toast.service';
import { WebrtcAudioService } from '@services/webrtc-audio.service';
import { AudioCallDeliveryStatus, AudioCallReliableMeta, AudioCallReliableSendResult, SOCKET_EVENT_NAMES } from '@services/web-socket.interfaces';
import { ConnectServiceBase } from '@services/connect-service.base';
import { CallSessionService } from '@call/services/call-session.service';
import { AudioCallConnectionDebugService } from '@services/audio-call-connection-debug.service';

const MAX_RECONNECTION_ATTEMPT_COUNT: number = 5;
const RECONNECTION_WAITING_TIME: number = 3500;

@Injectable({
  providedIn: 'root'
})
export class ConnectAudioService extends ConnectServiceBase implements OnDestroy {
  public readonly webrtcAudioService: WebrtcAudioService = inject(WebrtcAudioService);
  public readonly callSessionService: CallSessionService = inject(CallSessionService);
  private readonly webSocketController: WebSocketController = inject(WebSocketController);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly audioCallConnectionDebugService: AudioCallConnectionDebugService = inject(AudioCallConnectionDebugService);

  public isVideoSharing: boolean = false;
  public isVideoWatching: boolean = false;
  public customerId: number | null = null;
  public executorId: number | null = null;
  public orderId: number | null = null;
  public delayTime: number = RECONNECTION_WAITING_TIME;
  public triesToReconnect: number = 0;
  public maxReconnectionAttemptCount: number = MAX_RECONNECTION_ATTEMPT_COUNT;
  public mediaElement: HTMLVideoElement | HTMLAudioElement | null = null;
  public withAudio: boolean = true;
  public isAudioCallVideoEnabled: boolean = false;
  public isCameraToggleInProgress: boolean = false;
  public remoteStream$: Observable<MediaStream | null> = this.webrtcAudioService.remoteStream$;

  private connectTimeout: ReturnType<typeof setTimeout> | null = null;
  private customerReadySubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public customerReadyEvent$: Observable<boolean> = this.customerReadySubject.asObservable();
  private audioCallEventSubscription: Subscription | null = null;
  private readonly processedReliableMessageTtlMs: number = 120000;
  private processedCustomerReadyMessages: Map<string, number> = new Map();
  public stopStreamSubject: Subject<boolean> = new Subject<boolean>();
  public stopAccessSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public ngOnDestroy(): void {
    this.clearTimer();
    this.audioCallEventSubscription?.unsubscribe();
    this.audioCallEventSubscription = null;
  }

  public get inStream(): boolean {
    return this.isVideoSharing || this.isVideoWatching;
  }

  public get isScreenSharing(): boolean {
    return this.webrtcAudioService.isScreenSharing;
  }

  public get isVideoCallUsingFrontCamera(): boolean {
    const actualFacing = this.webrtcAudioService.getLocalVideoFacing();
    if (actualFacing) return actualFacing === 'user';
    return this.webrtcAudioService.getVideoCallCameraFacing() === 'user';
  }

  public hasRemoteVideoTrack(): boolean {
    return this.webrtcAudioService.hasRemoteVideoTrack();
  }

  public getLocalPreviewStream(): MediaStream | null {
    return this.webrtcAudioService.getLocalPreviewStream();
  }

  public subscribeToEvents(): void {
    if (this.audioCallEventSubscription) return;
    this.audioCallEventSubscription?.unsubscribe();

    this.audioCallEventSubscription = this.webSocketController.audioCallEvent.subscribe((res: any): void => {
      if (res?.type === SOCKET_EVENT_NAMES.AUDIO_CALL_CUSTOMER_READY) {
        this.audioCallConnectionDebugService.track(
          'connect',
          'customer_ready.received',
          {
            actionKey: 'call.audioCall.debugStatus.received',
            eventKey: 'call.audioCall.debugEvents.customerReady',
            tone: 'neutral',
          },
          {detail: res},
        );
        const meta: AudioCallReliableMeta | null = res?.meta ?? null;
        const messageId: string | null = typeof meta?.message_id === 'string' && meta.message_id.length > 0
          ? meta.message_id
          : null;
        if (messageId && this.isProcessedCustomerReadyMessage(messageId)) {
          this.audioCallConnectionDebugService.track(
            'connect',
            'customer_ready.duplicate',
            {
              actionKey: 'call.audioCall.debugStatus.confirmed',
              eventKey: 'call.audioCall.debugEvents.customerReady',
              tone: 'success',
            },
            {
              detail: {messageId, meta},
              updateCurrentStatus: false,
            },
          );
          if (meta) {
            this.webSocketController.emitAudioCallDeliveryReceipt(meta, 'duplicate');
          }
          return;
        }

        let status: AudioCallDeliveryStatus = 'ignored';
        let errorMessage: string | undefined;

        try {
          this.audioCallConnectionDebugService.track(
            'connect',
            'customer_ready.processing',
            {
              actionKey: 'call.audioCall.debugStatus.processing',
              eventKey: 'call.audioCall.debugEvents.customerReady',
              tone: 'neutral',
            },
            {
              detail: {
                to_id: res?.data?.to_id ?? null,
                order_id: res?.data?.order_id ?? null,
              },
            },
          );
          if (res?.data?.to_id && res?.data?.order_id) {
            const currentOrderId: number | null | undefined = this.callSessionService.selectedOrder?.id;
            const currentUserId: number | null | undefined = this.callSessionService.selectedOrder?.myUserId;

            if (!currentOrderId || res.data.order_id !== currentOrderId) {
              status = 'ignored';
            } else if (currentUserId && res.data.to_id !== currentUserId) {
              status = 'ignored';
            } else {
              this.customerReadySubject.next(true);
              status = 'processed';
            }
          } else {
            status = 'ignored';
          }
        } catch (error) {
          status = 'failed';
          errorMessage = error instanceof Error ? error.message : 'Failed to process customer ready event';
        }

        if (messageId && status === 'processed') {
          this.markCustomerReadyMessageProcessed(messageId);
        }
        this.trackCustomerReadyStatus(status, {
          messageId,
          errorMessage,
          order_id: res?.data?.order_id ?? null,
          to_id: res?.data?.to_id ?? null,
        });
        if (meta) {
          this.webSocketController.emitAudioCallDeliveryReceipt(meta, status, errorMessage);
        }
      }
    });
  }

  private cleanupProcessedCustomerReadyMessages(): void {
    const now: number = Date.now();
    this.processedCustomerReadyMessages.forEach((expiresAt: number, messageId: string): void => {
      if (expiresAt > now) return;
      this.processedCustomerReadyMessages.delete(messageId);
    });
  }

  private isProcessedCustomerReadyMessage(messageId: string): boolean {
    this.cleanupProcessedCustomerReadyMessages();
    const expiresAt: number | undefined = this.processedCustomerReadyMessages.get(messageId);
    if (!expiresAt) return false;
    if (expiresAt <= Date.now()) {
      this.processedCustomerReadyMessages.delete(messageId);
      return false;
    }
    return true;
  }

  private markCustomerReadyMessageProcessed(messageId: string): void {
    this.cleanupProcessedCustomerReadyMessages();
    this.processedCustomerReadyMessages.set(messageId, Date.now() + this.processedReliableMessageTtlMs);
  }

  private trackCustomerReadyStatus(
    status: AudioCallDeliveryStatus,
    detail: {
      messageId: string | null;
      errorMessage?: string;
      order_id?: number | null;
      to_id?: number | null;
    },
  ): void {
    if (status === 'processed') {
      this.audioCallConnectionDebugService.track(
        'connect',
        'customer_ready.processed',
        {
          actionKey: 'call.audioCall.debugStatus.ready',
          eventKey: 'call.audioCall.debugEvents.customerReady',
          tone: 'success',
        },
        {detail},
      );
      return;
    }

    if (status === 'ignored') {
      this.audioCallConnectionDebugService.track(
        'connect',
        'customer_ready.ignored',
        {
          actionKey: 'call.audioCall.debugStatus.ignored',
          eventKey: 'call.audioCall.debugEvents.customerReady',
          tone: 'warning',
        },
        {detail},
      );
      return;
    }

    if (status === 'failed') {
      this.audioCallConnectionDebugService.track(
        'connect',
        'customer_ready.failed',
        {
          actionKey: 'call.audioCall.debugStatus.failed',
          eventKey: 'call.audioCall.debugEvents.customerReady',
          tone: 'error',
        },
        {detail},
      );
    }
  }

  private async emitAudioCallExitWithDiagnostics(partnerId: number, orderId: number, context: string): Promise<void> {
    const result: AudioCallReliableSendResult = await this.webSocketController.emitAudioCallExit(partnerId, orderId);
    if (result.delivered) return;

    console.warn(`[ConnectAudioService] audioCallExit is not delivered (${context})`, {
      partnerId,
      orderId,
      result,
    });
  }

  private isRecoverableExecutorInitError(error: unknown): boolean {
    return error instanceof Error
      && error.message.includes('[WebrtcAudioService] Offer was not delivered (init_connection)');
  }

  public async startSharingByExecutor(
    customerId: number,
    orderId: number,
    mediaElement: HTMLVideoElement | HTMLAudioElement
  ): Promise<void> {
    this.audioCallConnectionDebugService.track(
      'connect',
      'executor.start_sharing',
      {
        actionKey: 'call.audioCall.debugStatus.initializingPeer',
        eventKey: 'call.audioCall.debugEvents.peer',
        tone: 'neutral',
      },
      {
        detail: {
          customerId,
          orderId,
        },
      },
    );
    this.customerId = customerId;
    this.orderId = orderId;
    this.mediaElement = mediaElement;
    this.isAudioCallVideoEnabled = false;
    this.withAudio = true;

    await this.initSharing();
  }

  private async initSharing(): Promise<void> {
    if (this.connectTimeout) {
      clearTimeout(this.connectTimeout);
    }
    this.triesToReconnect = 0;
    this.isVideoSharing = true;
    try {
      await this.webrtcAudioService.initPeerConnection(
        this.customerId,
        this.orderId,
        this.mediaElement,
        false,
        false,
        (): void => this.changeCallbackForExecutor(),
        async (error: unknown): Promise<void> => await this.errorHandlerCallbackForExecutor(error)
      );
    } catch (error) {
      if (!this.isRecoverableExecutorInitError(error)) {
        throw error;
      }

      this.audioCallConnectionDebugService.track(
        'connect',
        'executor.init_offer_retry_scheduled',
        {
          actionKey: 'call.audioCall.debugStatus.retryConnection',
          eventKey: 'call.audioCall.debugEvents.offer',
          tone: 'warning',
        },
        {
          detail: {
            customerId: this.customerId,
            orderId: this.orderId,
            errorMessage: error.message,
          },
        },
      );

      await this.errorHandlerCallbackForExecutor(error);
    }
  }

  private changeCallbackForExecutor(): void {
    if (this.webrtcAudioService.isConnected) {
      this.isVideoSharing = true;
      this.triesToReconnect = 0;
      if (this.connectTimeout) {
        clearTimeout(this.connectTimeout);
      }
    }
  }

  private async errorHandlerCallbackForExecutor(error: unknown): Promise<void> {
    console.error('errorHandlerCallbackForExecutor', error);
    if (error) {
      console.error(error);
      await this.stopSharing();
      this.triesToReconnect++;
      if (this.triesToReconnect <= this.maxReconnectionAttemptCount) {
        if (this.connectTimeout) {
          clearTimeout(this.connectTimeout);
        }
        this.connectTimeout = setTimeout((): void => {
          void this.initSharing();
        }, this.delayTime);
      } else {
        console.warn('Attempts to reconnect have ended');
      }
    }
  }

  public async stopSharingForExecutor(sendToPartner: boolean): Promise<void> {
    this.clearTimer();
    await this.stopSharing();

    if (sendToPartner) {
      const partnerId: number | null | undefined = this.callSessionService.selectedOrder?.partnerId ?? this.customerId;
      const orderId: number | null | undefined = this.callSessionService.selectedOrder?.id ?? this.orderId;
      if (!partnerId || !orderId) {
        await this.toastService.error('Partner ID or order ID is not defined!');
        console.error('partnerId or orderId is not defined!');
      } else {
        void this.emitAudioCallExitWithDiagnostics(partnerId, orderId, 'stopSharingForExecutor');
      }
    }
    if (this.connectTimeout) {
      clearTimeout(this.connectTimeout);
    }
    this.orderId = null;
    this.customerId = null;

    if (this.mediaElement) {
      this.mediaElement.pause();
      this.mediaElement.srcObject = null;
      this.mediaElement = null;
    }
  }

  public async stopSharing(): Promise<void> {
    this.webrtcAudioService.stopPeerConnection();
    this.customerReadySubject.next(false);
    this.isVideoSharing = false;
    this.isAudioCallVideoEnabled = false;
    this.withAudio = true;
  }

  public async startWatchingByCustomer(
    executorId: number,
    orderId: number,
    mediaElement: HTMLVideoElement | HTMLAudioElement
  ): Promise<void> {
    this.executorId = executorId;
    this.orderId = orderId;
    this.mediaElement = mediaElement;
    this.isAudioCallVideoEnabled = false;

    if (this.mediaElement) {
      this.mediaElement.muted = false;
    }
    this.withAudio = true;

    await this.initWatching();
  }

  private async initWatching(): Promise<void> {
    if (this.connectTimeout) {
      clearTimeout(this.connectTimeout);
    }
    this.triesToReconnect = 0;

    await this.webrtcAudioService.initPeerConnection(
      this.executorId,
      this.orderId,
      this.mediaElement,
      true,
      false,
      (): void => this.changeCallbackForCustomer(),
      (error: unknown): void => this.errorHandlerCallbackForCustomer(error)
    );

    this.isVideoWatching = true;
  }

  private changeCallbackForCustomer(): void {
    if (this.webrtcAudioService.isConnected) {
      this.isVideoWatching = true;
      if (this.connectTimeout) {
        clearTimeout(this.connectTimeout);
      }
    }
  }

  private errorHandlerCallbackForCustomer(error: unknown): void {
    console.error('errorHandlerCallbackForCustomer', error);
    if (error) {
      console.error(error);
      let waitingTime: number;
      waitingTime = this.maxReconnectionAttemptCount * this.delayTime;
      this.triesToReconnect = 1;
      if (this.connectTimeout) {
        clearTimeout(this.connectTimeout);
      }
      this.connectTimeout = setTimeout(() => {
        console.warn('Attempts to reconnect have ended');
      }, waitingTime + this.delayTime);
    }
  }

  public async stopWatchingByCustomer(sendToPartner: boolean): Promise<void> {
    this.clearTimer();
    await this.stopWatching();

    if (sendToPartner) {
      const partnerId: number | null | undefined = this.callSessionService.selectedOrder?.partnerId ?? this.executorId;
      const orderId: number | null | undefined = this.callSessionService.selectedOrder?.id ?? this.orderId;
      if (!partnerId || !orderId) {
        await this.toastService.error('Partner ID or order ID is not defined!');
        console.error('partnerId or orderId is not defined!');
      } else {
        void this.emitAudioCallExitWithDiagnostics(partnerId, orderId, 'stopWatchingByCustomer');
      }
    }
    if (this.connectTimeout) {
      clearTimeout(this.connectTimeout);
    }
    this.orderId = null;
    this.executorId = null;

    if (this.mediaElement) {
      this.mediaElement.pause();
      this.mediaElement.srcObject = null;
      this.mediaElement = null;
    }
  }

  public async stopWatching(): Promise<void> {
    this.webrtcAudioService.stopPeerConnection();
    this.isVideoWatching = false;
    this.isAudioCallVideoEnabled = false;
    this.withAudio = true;
  }

  public toggleMic(): void {
    this.withAudio = !this.withAudio;
    this.webrtcAudioService.toggleMic(this.withAudio);
  }

  public async toggleInputDevice(): Promise<void> {
    await this.webrtcAudioService.toggleInputDevice();
  }

  public async setAudioInputDevice(deviceId: string): Promise<void> {
    await this.webrtcAudioService.setInputDevice(deviceId);
  }

  public async getAudioInputDevices(): Promise<MediaDeviceInfo[]> {
    return await this.webrtcAudioService.listAudioInputDevices();
  }

  public getCurrentAudioInputDeviceId(): string | null {
    return this.webrtcAudioService.getCurrentAudioInputDeviceId();
  }

  public toggleAudioMode(): void {
    this.webrtcAudioService.toggleAudioMode();
  }

  public async toggleCameraForAudioCall(): Promise<void> {
    if (this.isCameraToggleInProgress) return;

    this.isCameraToggleInProgress = true;
    const wasEnabled: boolean = this.isAudioCallVideoEnabled;

    try {
      if (wasEnabled) {
        if (this.webrtcAudioService.isScreenSharing) {
          // Camera toggle should not stop active screen sharing; it only controls
          // whether we restore the camera once sharing is stopped.
          this.webrtcAudioService.disableCameraRestoreAfterScreenShare();
        } else {
          await this.webrtcAudioService.disableLocalVideo();
        }

        this.isAudioCallVideoEnabled = false;
        return;
      }

      if (this.webrtcAudioService.isScreenSharing) {
        await this.webrtcAudioService.stopScreenShare();
      }

      await this.webrtcAudioService.enableLocalVideo();
      this.isAudioCallVideoEnabled = true;
    } catch (error) {
      console.error('Failed to toggle camera for audio call', error);
      this.isAudioCallVideoEnabled = wasEnabled;
      throw error;
    } finally {
      this.isCameraToggleInProgress = false;
    }
  }

  public async switchCameraForVideoCall(): Promise<void> {
    if (this.isCameraToggleInProgress || !this.isAudioCallVideoEnabled) return;

    this.isCameraToggleInProgress = true;
    try {
      await this.webrtcAudioService.toggleVideoCallCameraFacing();
    } finally {
      this.isAudioCallVideoEnabled = this.webrtcAudioService.hasLocalVideoTrack();
      this.isCameraToggleInProgress = false;
    }
  }

  public async toggleScreenShare(): Promise<void> {
    if (this.webrtcAudioService.isScreenSharing) {
      await this.webrtcAudioService.stopScreenShare();
      return;
    }
    await this.webrtcAudioService.startScreenShare();
  }

  public async startTimer(): Promise<void> {
    if (this.timerSubscription) return;

    this.streamTimeSubject.next('00:00');

    this.timerSubscription = interval(1000).pipe(
      tap((): void => {
        if (this.webrtcAudioService.isConnected) {
          if (!this.webrtcAudioService.streamStartTime) return;

          this.streamTime++;

          const now: number = Date.now();
          const elapsed: number = now - this.webrtcAudioService.streamStartTime;

          const seconds: number = Math.floor((elapsed % (1000 * 60)) / 1000);

          const hours: number = Math.floor(elapsed / (1000 * 60 * 60));
          const minutes: number = Math.floor((elapsed % (1000 * 60 * 60)) / (1000 * 60));

          const formattedTime: string = this.formatTime(hours, minutes, seconds);
          this.streamTimeSubject.next(formattedTime);

          const totalSeconds: number = Math.floor(elapsed / 1000);
          this.totalSecondsSubject.next(totalSeconds * 1000);
        }
      })
    ).subscribe();
  }
}
